import { Component, OnInit } from '@angular/core';
import { ToastController, AlertController, Platform } from '@ionic/angular';
import { ScreenOrientation } from '@ionic-native/screen-orientation/ngx';
import { Space, Page } from 'app/interfaces/api/types';
import { Environment } from 'environments/environment';
import { Entry } from '@ionic-native/file/ngx';
import { arrayUnique, Progress } from 'app/utils';

import {
  StorageService,
  TranslatorService,
  SettingsService,
  MediaDownloadResult
} from 'app/services';

type ExtendedSpace = Space & {
  offlineMedia?: number;
  totalMedia?: number;
  offlineMap?: boolean;
  offlineContent?: boolean;
};

@Component({
  selector: 'app-settings',
  templateUrl: './settings.page.html',
  styleUrls: ['./settings.page.scss'],
})

export class SettingsPage implements OnInit {
  public readonly LANGUAGES: string[] = Environment.getLanguages();

  public useOfflineContent: boolean;
  public useOfflineMap: boolean;
  public spaces: ExtendedSpace[] = [];
  public pages: Page[] = [];
  public media: string[] = [];

  public pageTotalMedia: number;
  public pageOfflineMedia: number;

  public didLoad: boolean = false;

  constructor(
    private readonly alertController: AlertController,
    private readonly toastController: ToastController,
    private readonly storageService: StorageService,
    private readonly translator: TranslatorService,
    private readonly screenOrientation: ScreenOrientation,
    private readonly settings: SettingsService,
    private readonly platform: Platform
  ) { }

  ngOnInit(): void {
    Promise.all([
      this.platform.ready(),
      this.storageService.ready()
    ]).then(() => {
      this.useOfflineContent = this.settings.getOfflineContentActive();
      this.useOfflineMap = this.settings.getOfflineMapActive();

      this.loadData().finally(() => this.didLoad = true);
    });
  }

  private async loadData(): Promise<void> {
    await this.loadMedia();

    await Promise.all([
      this.loadSpaces(),
      this.loadPages()
    ]);
  }

  private async loadSpaces(): Promise<void> {
    const spaces: ExtendedSpace[] = await this.storageService.getSpaces();

    for (const space of spaces) {
      const media: string[] = await this.storageService.getSpaceMedia(space);

      space.offlineMap = await this.storageService.getSpaceMapOffline(space);
      space.totalMedia = media.length;
      space.offlineMedia = media
        .map((url: string) => this.storageService.maybeEncodeURI(url.split('/').pop()))
        .filter((filename: string) => this.media.includes(filename))
        .length;
    }

    this.spaces = spaces;
  }

  private async loadPages(): Promise<void> {
    this.pages = await this.storageService.getPages();

    const media: string[] = await this.getPagesMedia(this.pages);

    this.pageTotalMedia = media.length;
    this.pageOfflineMedia = media
      .map((url: string) => this.storageService.maybeEncodeURI(url.split('/').pop()))
      .filter((filename: string) => this.media.includes(filename))
      .length;
  }

  private async loadMedia(): Promise<void> {
    const entries: Entry[] = await this.storageService.listMedia();

    this.media = entries.map(({ name }) => name);
  }

  private async getPagesMedia(pages: Page[]): Promise<string[]> {
    let media: string[] = [];

    for (const page of pages) {
      media = media.concat(await this.storageService.getPageMedia(page));
    }

    return arrayUnique<string>(media);
  }

  private async lockScreenOrientation(): Promise<void> {
    await this.screenOrientation.lock(this.screenOrientation.type);
  }

  private async unlockScreenOrientation(): Promise<void> {
    await this.screenOrientation.unlock();
  }

  public async toggleUseOfflineContent(e: boolean): Promise<void> {
    this.lockScreenOrientation();

    if (e) {
      const alert = await this.alertController.create({
        header: this.translator.translate('preparing_offline_content'),
        backdropDismiss: false,
        buttons: [
          {
            text: this.translator.translate('cancel'),
            handler: () => this.cancel()
          }
        ]
      });

      await alert.present();

      try {
        for (const language of this.LANGUAGES) {
          const prefix: string = '[' + language.toUpperCase() + ']';

          const onProgress: Function = (p: Progress) => alert.message = `${prefix} ${p.progress} / ${p.total}`;

          await this.storageService.withoutCache(() => this.storageService.downloadPosts(language, onProgress));
        }

        await alert.dismiss();

        this.showToast('content_successfully_downloaded');

        this.settings.setOfflineContentActive(e);
      } catch(err) {
        await alert.dismiss();

        this.useOfflineContent = false;
        this.settings.setOfflineContentActive(false);
        this.showToast('failed_to_download_content', 'danger');
      }
    } else {
      this.settings.setOfflineContentActive(e);
    }

    this.unlockScreenOrientation();
  }

  public toggleUseOfflineMap(e: boolean): void {
    this.settings.setOfflineMapActive(e);
  }

  public async downloadAllPageMedia(): Promise<void> {
    this.lockScreenOrientation();

    const alert = await this.alertController.create({
      header: this.translator.translate('downloading_content'),
      message: this.translator.translate('this_may_take_a_while') + '...',
      backdropDismiss: false,
      buttons: [
        {
          text: this.translator.translate('cancel'),
          handler: () => this.cancel()
        }
      ]
    });

    await alert.present();

    const media: string[] = await this.getPagesMedia(this.pages);
    const onProgress: Function = (p: Progress) => alert.message = `${p.progress} / ${p.total}`;

    await this.storageService.downloadMedia(media, onProgress)
      .then(async (res: MediaDownloadResult) => {
        await this.loadData();

        alert.dismiss();

        this.unlockScreenOrientation();

        document.dispatchEvent(new CustomEvent('rdi::downloadMedia', {
          detail: {
            slug: 'page'
          }
        }));

        if (res.failed > 0) {
          await this.showToast(this.translator.translate('failed_downloads') + `: ${res.failed}`, 'danger')
            .then((toast) => toast.onWillDismiss());
        }

        if (res.downloads > 0) {
          await this.showToast(this.translator.translate('successful_downloads') + `: ${res.downloads}`);
        }
      });
  }

  public async downloadSpaceMedia(space: ExtendedSpace): Promise<void> {
    this.lockScreenOrientation();

    const alert = await this.alertController.create({
      header: this.translator.translate('downloading_content'),
      message: this.translator.translate('this_may_take_a_while') + '...',
      backdropDismiss: false,
      buttons: [
        {
          text: this.translator.translate('cancel'),
          handler: () => this.cancel()
        }
      ]
    });

    await alert.present();

    const media: string[] = await this.storageService.getSpaceMedia(space);
    const onProgress: Function = (p: Progress) => alert.message = `${p.progress} / ${p.total}`;

    await this.storageService.downloadMedia(media, onProgress)
      .then(async (res: MediaDownloadResult) => {
        await this.loadData();

        alert.dismiss();

        this.unlockScreenOrientation();

        document.dispatchEvent(new CustomEvent('rdi::downloadMedia', {
          detail: {
            slug: space.slug
          }
        }));

        if (res.failed > 0) {
          await this.showToast(this.translator.translate('failed_downloads') + `: ${res.failed}`, 'danger')
            .then((toast) => toast.onWillDismiss());
        }

        if (res.downloads > 0) {
          await this.showToast(this.translator.translate('successful_downloads') + `: ${res.downloads}`);
        }
      });
  }

  public async downloadSpaceMap(space: ExtendedSpace): Promise<void> {
    this.lockScreenOrientation();

    const alert = await this.alertController.create({
      header: this.translator.translate('downloading_map'),
      message: this.translator.translate('this_may_take_a_while') + '...',
      backdropDismiss: false,
      buttons: [
        {
          text: this.translator.translate('cancel'),
          handler: () => this.cancel()
        }
      ]
    });

    await alert.present();

    await this.storageService.downloadSpaceMap(space)
      .then(() => this.loadSpaces())
      .then(() => this.showToast('download_successful'))
      .then(() => {
        document.dispatchEvent(new CustomEvent('rdi::downloadMap', {
          detail: {
            slug: space.slug
          }
        }));
      })
      .catch(() => this.showToast('failed_to_download_map', 'danger'))
      .finally(() => {
        alert.dismiss();
        this.unlockScreenOrientation();
      });
  }

  public async clearLocalData(): Promise<void> {
    const alert = await this.alertController.create({
      header: this.translator.translate('are_you_sure') + '?',
      message: this.translator.translate('this_will_delete_all_local_data'),
      backdropDismiss: false,
      buttons: [
        {
          text: this.translator.translate('cancel'),
          handler: () => alert.dismiss()
        },
        {
          text: this.translator.translate('yes'),
          handler: () => {
            this.toggleUseOfflineContent(false);
            this.toggleUseOfflineMap(false);

            Promise.all([
              this.storageService.clear(),
              this.storageService.renewDirectories()
            ]).finally(() => this.cancel());
          }
        }
      ]
    });

    await alert.present();
  }

  private async showToast(message: string, color: string = 'menucolor'): Promise<HTMLIonToastElement> {
    const toast = await this.toastController.create({
      message: this.translator.translate(message),
      showCloseButton: true,
      closeButtonText: this.translator.translate('close'),
      duration: 2000,
      color
    });

    await toast.present();

    return toast;
  }

  private cancel(): void {
    window.location.reload();
  }
}
